import { useState } from 'react';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useVerifyToken, useAcceptInvite } from '@/hooks/use-accept-invite';
import { useAuthStore } from '@/stores/auth-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

export function SetPasswordPage() {
  useDocumentTitle('Set Your Password');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { data: tokenData, isLoading: isVerifying, isError: tokenInvalid } = useVerifyToken(token);
  const acceptInvite = useAcceptInvite();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const isTokenMissing = !token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      const res = await acceptInvite.mutateAsync({ token, password });

      if (res.success && res.data) {
        // Auto-login: set the auth state with the returned JWT
        useAuthStore.setState({
          token: res.data.token,
          refreshToken: null,
          user: res.data.user,
          isAuthenticated: true,
        });
        setSuccess(true);
        toast({
          title: 'Account created!',
          description: `Welcome, ${res.data.user.name}!`,
          variant: 'success',
        });
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to set up your account. The link may have expired.');
    }
  };

  // ─── Token type routing ─────────────────────────────────
  // If token is quote_invite, redirect to accept-quote to view the quote first
  const inviteData = tokenData?.data;
  if (inviteData && inviteData.type === 'quote_invite') {
    return <Navigate to={`/accept-quote?token=${token}`} replace />;
  }

  // ─── Loading state ──────────────────────────────────────
  if (!isTokenMissing && isVerifying) {
    return (
      <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
        <div className='text-center'>
          <Loader2 className='w-8 h-8 animate-spin text-dyndev-green mx-auto mb-3' />
          <p className='text-sm text-[var(--text-secondary)]'>Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  // ─── Invalid / expired token ────────────────────────────
  if (isTokenMissing || tokenInvalid) {
    return (
      <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
        <div className='w-full max-w-md relative z-10'>
          <div className='text-center mb-8'>
            <h1 className='text-4xl font-bold tracking-tight mb-2'>
              Dyn<span className='text-dyndev-green'>Dev</span>
            </h1>
            <p className='text-[var(--text-secondary)] text-sm'>Project Management Portal</p>
          </div>
          <Card className='shadow-xl'>
            <CardHeader>
              <CardTitle className='text-xl flex items-center gap-2'>
                <AlertCircle size={20} className='text-red-400' />
                Invalid Link
              </CardTitle>
            </CardHeader>
            <CardContent className='text-center'>
              <p className='text-sm text-[var(--text-secondary)] mb-6'>
                This invitation link is invalid or has expired. Please contact your project manager
                for a new invitation.
              </p>
              <Link
                to='/login'
                className='inline-flex items-center gap-1 text-sm text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium'
              >
                <ArrowLeft size={14} />
                Back to Sign In
              </Link>
            </CardContent>
          </Card>
          <p className='text-center mt-6 text-xs text-[var(--text-secondary)]'>
            &copy; {new Date().getFullYear()} DynDev.
          </p>
        </div>
      </div>
    );
  }

  // ─── Success state ──────────────────────────────────────
  if (success) {
    return (
      <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
        <div className='w-full max-w-md relative z-10'>
          <div className='text-center mb-8'>
            <h1 className='text-4xl font-bold tracking-tight mb-2'>
              Dyn<span className='text-dyndev-green'>Dev</span>
            </h1>
            <p className='text-[var(--text-secondary)] text-sm'>Project Management Portal</p>
          </div>
          <Card className='shadow-xl'>
            <CardHeader>
              <CardTitle className='text-xl'>Account Ready!</CardTitle>
            </CardHeader>
            <CardContent className='text-center'>
              <div className='flex justify-center mb-6'>
                <div className='w-16 h-16 rounded-full bg-dyndev-green/10 flex items-center justify-center'>
                  <CheckCircle size={32} className='text-dyndev-green' />
                </div>
              </div>
              <p className='text-sm text-[var(--text-secondary)] mb-6'>
                Your account has been created successfully. You are now signed in.
              </p>
              <Link
                to='/'
                className='inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-dyndev-green text-white text-sm font-medium hover:bg-dyndev-green/90 transition-colors'
              >
                Go to Dashboard
                <ArrowLeft size={14} className='rotate-180' />
              </Link>
            </CardContent>
          </Card>
          <p className='text-center mt-6 text-xs text-[var(--text-secondary)]'>
            &copy; {new Date().getFullYear()} DynDev.
          </p>
        </div>
      </div>
    );
  }

  // ─── Password form ──────────────────────────────────────
  return (
    <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
      {/* Noise overlay */}
      <div className='bg-noise opacity-[0.02] pointer-events-none fixed inset-0' />

      <div className='w-full max-w-md relative z-10'>
        {/* Logo */}
        <div className='text-center mb-8'>
          <h1 className='text-4xl font-bold tracking-tight mb-2'>
            Dyn<span className='text-dyndev-green'>Dev</span>
          </h1>
          <p className='text-[var(--text-secondary)] text-sm'>Project Management Portal</p>
        </div>

        <Card className='shadow-xl'>
          <CardHeader>
            <CardTitle className='text-xl'>Set Your Password</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Welcome message */}
            {inviteData && (
              <div className='mb-6 px-4 py-3 rounded-lg bg-dyndev-green/5 border border-dyndev-green/10'>
                <p className='text-sm text-[var(--text-primary)]'>
                  Hi <strong>{inviteData.name || inviteData.email}</strong>,
                </p>
                <p className='text-xs text-[var(--text-secondary)] mt-1'>
                  Choose a password to set up your DynDev Portal account.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-4'>
              {error && (
                <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm'>
                  {error}
                </div>
              )}

              {/* Password */}
              <div className='space-y-1.5'>
                <Label htmlFor='password'>Password *</Label>
                <div
                  className={`relative rounded-lg transition-all duration-300 ${
                    passwordFocused ? 'ring-2 ring-dyndev-green/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''
                  }`}
                >
                  <Input
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    placeholder='At least 6 characters'
                    required
                    className='pr-10'
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors'
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className='space-y-1.5'>
                <Label htmlFor='confirm-password'>Confirm Password *</Label>
                <div className='relative'>
                  <Input
                    id='confirm-password'
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    placeholder='Re-enter your password'
                    required
                    className='pr-10'
                  />
                  <button
                    type='button'
                    onClick={() => setShowConfirm(!showConfirm)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors'
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button
                type='submit'
                disabled={acceptInvite.isPending || !password.trim() || !confirmPassword.trim()}
                size='pill'
                className='w-full'
              >
                {acceptInvite.isPending && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
                {acceptInvite.isPending ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            <div className='text-center mt-6'>
              <Link
                to='/login'
                className='inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-dyndev-green transition-colors'
              >
                <ArrowLeft size={14} />
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className='text-center mt-6 text-xs text-[var(--text-secondary)]'>
          &copy; {new Date().getFullYear()} DynDev.
        </p>
      </div>
    </div>
  );
}
